/* eslint-disable max-lines */
import { useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { LibraryStatsResponse } from "@/api/admin-api"

import {
  ChevronDown,
  ChevronUp,
  Cloud,
  Database,
  FolderUp,
  Globe,
  HardDrive,
  Loader2,
  MoreVertical,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { EmptyState } from "@/components/ui/empty-state"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import { AdminTableToolbar } from "./admin-table-toolbar"
import { LibraryTreeBackfillModal } from "./library-tree-backfill-modal"
import {
  useAdminLibraryStatsQuery,
  useAllActiveBackfillsQuery,
  useAllTreeCoverageQuery,
  useStartLibraryTreeBackfillMutation,
} from "../hooks/admin-queries"
import { adminQueryKeys } from "../hooks/admin-query-keys"
import { useAdminTableControls } from "../hooks/use-admin-table-controls"

export type LibrarySourceType =
  | "sharepoint"
  | "spo"
  | "spo-avtal"
  | "spo-list"
  | "spo-pages"
  | "blob"
  | "dynamics"
  | "localdocs"
  | "external"
  | string

export type LibraryTableItem = {
  id: string
  display_name: string
  description: string
  /** @deprecated Use search_enabled instead */
  enabled?: boolean
  search_enabled: boolean
  indexing_enabled: boolean
  document_count?: number
  access_configured?: boolean
  source?: string
  source_type?: LibrarySourceType
  exclude_patterns?: string[]
  // Azure resource names for cleanup
  index_name?: string
  output_container?: string
  output_table?: string
}

// Helper to get source type display info
function getSourceTypeInfo(sourceType?: LibrarySourceType) {
  switch (sourceType?.toLowerCase()) {
    case "sharepoint":
    case "spo":
      return { icon: Cloud, label: "SharePoint", color: "text-muted-foreground" }
    case "spo-avtal":
      return { icon: Cloud, label: "SPO Avtal", color: "text-muted-foreground" }
    case "spo-list":
      return { icon: Database, label: "SPO List", color: "text-muted-foreground" }
    case "spo-pages":
      return { icon: Cloud, label: "SPO Pages", color: "text-muted-foreground" }
    case "blob":
      return { icon: HardDrive, label: "Blob", color: "text-muted-foreground" }
    case "dynamics":
      return { icon: Globe, label: "Dynamics", color: "text-primary-ink" }
    case "localdocs":
      return { icon: FolderUp, label: "Local Docs", color: "text-warning-ink" }
    case "external":
      return { icon: Globe, label: "External", color: "text-muted-foreground" }
    default:
      return { icon: Database, label: sourceType || "—", color: "text-muted-foreground" }
  }
}

function getCoverageBadgeTone(coveragePct: number | null): string {
  // Design-token-driven so the badge tracks `--primary` (customer-themable
  // per DESIGN.md). Amber/warning has no semantic token yet, so the partial
  // tier keeps Tailwind amber with a dark-mode variant pending a future
  // `--warning` token.
  if (typeof coveragePct !== "number") return "bg-muted text-muted-foreground"
  if (coveragePct >= 100) return "bg-primary/10 text-primary"
  if (coveragePct >= 90) return "bg-warning/30 text-warning-ink "
  return "bg-destructive/10 text-destructive"
}

function useFormatRelativeTime() {
  const { t } = useTranslation()

  return (isoString: string | null | undefined): string => {
    if (!isoString) return ""
    const date = new Date(isoString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return t("adminDashboard.libraries.stats.relativeTime.justNow", "just now")
    if (diffMins < 60)
      return t("adminDashboard.libraries.stats.relativeTime.minutesAgo", "{{count}}m ago", {
        count: diffMins,
      })
    if (diffHours < 24)
      return t("adminDashboard.libraries.stats.relativeTime.hoursAgo", "{{count}}h ago", {
        count: diffHours,
      })
    return t("adminDashboard.libraries.stats.relativeTime.daysAgo", "{{count}}d ago", {
      count: diffDays,
    })
  }
}

function DiffBadge({ value, isGoodWhenNegative }: { value: number; isGoodWhenNegative?: boolean }) {
  if (value === 0) {
    return <span className="text-muted-foreground">—</span>
  }

  const isPositive = value > 0
  const isGood = isGoodWhenNegative ? !isPositive : isPositive

  return (
    <span className={isGood ? "text-success" : "text-warning-ink"}>
      {isPositive ? "+" : ""}
      {value.toLocaleString()}
    </span>
  )
}

function LibraryStatsPanel({
  stats,
  isLoading,
  error,
}: {
  stats: LibraryStatsResponse | undefined
  isLoading: boolean
  error: Error | null
}) {
  const { t } = useTranslation()
  const formatRelativeTime = useFormatRelativeTime()

  if (isLoading) {
    return (
      <div className="bg-muted/30 flex items-center justify-center rounded-lg border border-dashed p-6">
        <Loader2 className="text-muted-foreground mr-2 size-4 animate-spin" />
        <span className="text-muted-foreground text-sm">
          {t("adminDashboard.libraries.stats.loading", "Loading statistics...")}
        </span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border-destructive rounded-lg border p-4">
        <span className="text-destructive text-sm">{error.message}</span>
      </div>
    )
  }

  if (!stats || !stats.has_table) {
    return (
      <div className="bg-muted/30 rounded-lg border border-dashed p-4">
        <span className="text-muted-foreground text-sm">
          {t(
            "adminDashboard.libraries.stats.noTable",
            "No table storage configured for this library"
          )}
        </span>
      </div>
    )
  }

  if (!stats.stats) {
    return null
  }

  const statRows = [
    {
      label: t("adminDashboard.libraries.stats.indexed", "Indexed"),
      value: stats.stats.indexed,
      diff: stats.diffs?.indexed ?? 0,
      isGoodWhenNegative: false,
    },
    {
      label: t("adminDashboard.libraries.stats.failed", "Failed"),
      value: stats.stats.failed,
      diff: stats.diffs?.failed ?? 0,
      isGoodWhenNegative: true,
    },
    {
      label: t("adminDashboard.libraries.stats.quotaExceeded", "Quota Exceeded"),
      value: stats.stats.quota_exceeded,
      diff: stats.diffs?.quota_exceeded ?? 0,
      isGoodWhenNegative: true,
    },
    {
      label: t("adminDashboard.libraries.stats.oversizedDeferred", "Oversized (Deferred)"),
      value: stats.stats.oversized_deferred,
      diff: stats.diffs?.oversized_deferred ?? 0,
      isGoodWhenNegative: true,
    },
    {
      label: t("adminDashboard.libraries.stats.started", "Processing"),
      value: stats.stats.started,
      diff: stats.diffs?.started ?? 0,
      isGoodWhenNegative: false,
    },
  ]

  return (
    <div className="bg-muted/30 rounded-lg border p-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-medium">
          {t("adminDashboard.libraries.stats.title", "Index Statistics")}
        </h4>
        {stats.previous_check_at && (
          <span className="text-muted-foreground text-xs">
            {t("adminDashboard.libraries.stats.lastChecked", "Last checked")}:{" "}
            {formatRelativeTime(stats.previous_check_at)}
          </span>
        )}
      </div>

      <div className="space-y-2">
        {statRows.map((row) => (
          <div key={row.label} className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{row.label}</span>
            <div className="flex items-center gap-3">
              <span className="font-mono">{row.value.toLocaleString()}</span>
              {stats.diffs && (
                <DiffBadge isGoodWhenNegative={row.isGoodWhenNegative} value={row.diff} />
              )}
            </div>
          </div>
        ))}

        <div className="border-border my-2 border-t" />

        <div className="flex items-center justify-between text-sm font-medium">
          <span>{t("adminDashboard.libraries.stats.total", "Total")}</span>
          <span className="font-mono">{stats.stats.total.toLocaleString()}</span>
        </div>
      </div>
    </div>
  )
}

function LibraryRow({
  library,
  onToggleSearchEnabled,
  onToggleIndexingEnabled,
  onEditAccess,
  onEdit,
  onDelete,
  onBrowseDocuments,
  onOpenTreeBackfill,
  activeJobId,
  isStartingBackfill,
  treeCoverage,
  isUpdating,
  disableAll,
  isStatsExpanded,
  onToggleStats,
}: {
  library: LibraryTableItem
  onToggleSearchEnabled?: (libraryId: string, enabled: boolean) => void
  onToggleIndexingEnabled?: (libraryId: string, enabled: boolean) => void
  onEditAccess?: (libraryId: string) => void
  onEdit?: (library: LibraryTableItem) => void
  onDelete?: (library: LibraryTableItem) => void
  onBrowseDocuments?: (libraryId: string, libraryName: string) => void
  onOpenTreeBackfill?: (library: LibraryTableItem, existingJobId?: string | null) => void
  activeJobId: string | null
  isStartingBackfill: boolean
  treeCoverage: {
    total: number
    with_tree: number
    without_tree: number
    coverage_pct: number
  } | null
  isUpdating?: string
  disableAll?: boolean
  isStatsExpanded: boolean
  onToggleStats: () => void
}) {
  const { t } = useTranslation()
  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError,
  } = useAdminLibraryStatsQuery(isStatsExpanded ? library.id : null)

  const sourceInfo = getSourceTypeInfo(library.source_type)
  const SourceIcon = sourceInfo.icon

  const withoutTree = treeCoverage?.without_tree ?? null
  const totalTrees = treeCoverage?.total ?? null
  const hasCoverage = withoutTree !== null
  const withTree =
    typeof totalTrees === "number" && typeof withoutTree === "number"
      ? totalTrees - withoutTree
      : null
  const coveragePct = treeCoverage?.coverage_pct ?? null
  const badgeTone = getCoverageBadgeTone(coveragePct)
  const backfillDisabled =
    disableAll ||
    isStartingBackfill ||
    (!activeJobId && hasCoverage && withoutTree === 0) ||
    !onOpenTreeBackfill

  return (
    <div className="space-y-2">
      <div className="bg-card rounded-lg border lg:hidden" data-testid="admin-library-mobile-row">
        <div className="flex min-w-0 items-start gap-3 px-4 py-3">
          <span className="bg-muted flex size-10 shrink-0 items-center justify-center rounded-md">
            <SourceIcon aria-hidden="true" className={`size-5 ${sourceInfo.color}`} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <span className="line-clamp-2 font-medium">{library.display_name}</span>
              <span
                className={cn(
                  "ml-auto shrink-0 rounded-md px-2 py-1 text-xs font-medium",
                  library.search_enabled
                    ? "bg-success/10 text-success"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {library.search_enabled
                  ? t("adminDashboard.libraries.statusActive", "Active")
                  : t("adminDashboard.libraries.statusHidden", "Hidden")}
              </span>
            </div>
            <p className="text-muted-foreground truncate text-xs">{library.id}</p>
            {library.description ? (
              <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                {library.description}
              </p>
            ) : null}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label={t("adminDashboard.libraries.actions", "Library actions")}
                className="size-11 shrink-0"
                size="icon"
                type="button"
                variant="ghost"
              >
                <MoreVertical aria-hidden="true" className="size-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={onToggleStats}>
                {t("adminDashboard.libraries.stats.title", "Index Statistics")}
              </DropdownMenuItem>
              {onEdit ? (
                <DropdownMenuItem disabled={disableAll} onSelect={() => onEdit(library)}>
                  {t("adminDashboard.libraries.edit", "Edit library")}
                </DropdownMenuItem>
              ) : null}
              {onBrowseDocuments ? (
                <DropdownMenuItem
                  disabled={disableAll}
                  onSelect={() => onBrowseDocuments(library.id, library.display_name)}
                >
                  {t("adminDashboard.libraries.browseDocuments", "Browse documents")}
                </DropdownMenuItem>
              ) : null}
              {onOpenTreeBackfill ? (
                <DropdownMenuItem
                  disabled={backfillDisabled}
                  onSelect={() => onOpenTreeBackfill(library, activeJobId)}
                >
                  {activeJobId
                    ? t("libraries.treeBackfill.viewActive", "View running backfill")
                    : t("libraries.treeBackfill.open", "Backfill missing tree artifacts")}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                disabled={disableAll || !onEditAccess}
                onSelect={() => onEditAccess?.(library.id)}
              >
                {t("adminDashboard.access.access", { defaultValue: "Access" })}
              </DropdownMenuItem>
              {onDelete ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    disabled={disableAll}
                    onSelect={() => onDelete(library)}
                  >
                    {t("adminDashboard.libraries.delete", "Delete library")}
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="border-border grid grid-cols-4 items-center gap-1 border-t px-3 py-2 text-xs">
          <div className="flex min-h-11 flex-col justify-center px-1">
            <span className="text-muted-foreground">Docs</span>
            <span className="font-medium tabular-nums">
              {library.document_count?.toLocaleString() ?? "—"}
            </span>
          </div>
          <div className="flex min-h-11 flex-col justify-center px-1">
            <span className="text-muted-foreground">Trees</span>
            <span className="font-medium tabular-nums">
              {hasCoverage && typeof withTree === "number" && typeof totalTrees === "number"
                ? `${withTree}/${totalTrees}`
                : "—"}
            </span>
          </div>
          <div className="hover:bg-muted/50 flex min-h-11 flex-col items-center justify-center rounded-md">
            <span className="text-muted-foreground">Search</span>
            <Label
              className="flex size-11 cursor-pointer items-center justify-center rounded-md"
              htmlFor={`library-search-${library.id}`}
            >
              <Switch
                aria-label={`${library.display_name} search`}
                checked={library.search_enabled}
                disabled={disableAll || isUpdating === library.id || !onToggleSearchEnabled}
                id={`library-search-${library.id}`}
                onCheckedChange={(checked) => onToggleSearchEnabled?.(library.id, checked)}
              />
            </Label>
          </div>
          <div className="hover:bg-muted/50 flex min-h-11 flex-col items-center justify-center rounded-md">
            <span className="text-muted-foreground">Indexing</span>
            <Label
              className="flex size-11 cursor-pointer items-center justify-center rounded-md"
              htmlFor={`library-indexing-${library.id}`}
            >
              <Switch
                aria-label={`${library.display_name} indexing`}
                checked={library.indexing_enabled}
                disabled={disableAll || isUpdating === library.id || !onToggleIndexingEnabled}
                id={`library-indexing-${library.id}`}
                onCheckedChange={(checked) => onToggleIndexingEnabled?.(library.id, checked)}
              />
            </Label>
          </div>
        </div>
      </div>

      <div className="bg-card hover:bg-accent/50 hidden grid-cols-12 items-center gap-3 rounded-lg border px-4 py-3 text-sm transition-colors lg:grid">
        {/* Name & ID */}
        <div className="col-span-2 flex flex-col gap-0.5 overflow-hidden">
          <span className="truncate font-medium">{library.display_name}</span>
          <span className="text-muted-foreground truncate text-xs">{library.id}</span>
        </div>
        {/* Type */}
        <div className="col-span-1 flex items-center justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1">
                <SourceIcon className={`size-4 ${sourceInfo.color}`} />
              </div>
            </TooltipTrigger>
            <TooltipContent>{sourceInfo.label}</TooltipContent>
          </Tooltip>
        </div>
        {/* Description */}
        <div className="text-muted-foreground col-span-3 line-clamp-2 text-xs">
          {library.description || "—"}
        </div>
        {/* Document Count */}
        <div className="col-span-1 text-center font-mono text-xs tabular-nums">
          {library.document_count?.toLocaleString() ?? "—"}
        </div>
        {/* Tree Coverage */}
        <div className="col-span-1 flex items-center justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={`inline-flex min-w-[72px] items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums ${badgeTone}`}
              >
                {hasCoverage && typeof withTree === "number" && typeof totalTrees === "number"
                  ? `${withTree}/${totalTrees}`
                  : "—"}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {hasCoverage && typeof withoutTree === "number"
                ? t("libraries.treeCoverage.tooltip", {
                    defaultValue: "{{missing}} missing tree artifacts",
                    missing: withoutTree,
                  })
                : t(
                    "libraries.treeCoverage.tooltipUnavailable",
                    "Tree coverage unavailable for this library"
                  )}
            </TooltipContent>
          </Tooltip>
        </div>
        {/* Actions */}
        <div className="col-span-1 flex items-center justify-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label={t("adminDashboard.libraries.actions", "Library actions")}
                size="icon"
                type="button"
                variant="ghost"
              >
                <MoreVertical aria-hidden="true" className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={onToggleStats}>
                {t("adminDashboard.libraries.stats.title", "Index Statistics")}
              </DropdownMenuItem>
              {onEdit ? (
                <DropdownMenuItem disabled={disableAll} onSelect={() => onEdit(library)}>
                  {t("adminDashboard.libraries.edit", "Edit library")}
                </DropdownMenuItem>
              ) : null}
              {onBrowseDocuments ? (
                <DropdownMenuItem
                  disabled={disableAll}
                  onSelect={() => onBrowseDocuments(library.id, library.display_name)}
                >
                  {t("adminDashboard.libraries.browseDocuments", "Browse documents")}
                </DropdownMenuItem>
              ) : null}
              {onOpenTreeBackfill ? (
                <DropdownMenuItem
                  disabled={backfillDisabled}
                  onSelect={() => onOpenTreeBackfill(library, activeJobId)}
                >
                  {activeJobId
                    ? t("libraries.treeBackfill.viewActive", "View running backfill")
                    : t("libraries.treeBackfill.open", "Backfill missing tree artifacts")}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                disabled={disableAll || !onEditAccess}
                onSelect={() => onEditAccess?.(library.id)}
              >
                {t("adminDashboard.access.access", { defaultValue: "Access" })}
              </DropdownMenuItem>
              {onDelete ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    disabled={disableAll}
                    onSelect={() => onDelete(library)}
                  >
                    {t("adminDashboard.libraries.delete", "Delete library")}
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {/* Search Enabled Toggle */}
        <div className="col-span-1 flex items-center justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Switch
                  aria-label={`${library.display_name} search`}
                  checked={library.search_enabled}
                  disabled={disableAll || isUpdating === library.id || !onToggleSearchEnabled}
                  onCheckedChange={(checked) => onToggleSearchEnabled?.(library.id, checked)}
                />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {library.search_enabled
                ? t(
                    "adminDashboard.libraries.searchEnabledTooltip",
                    "Users can search this library"
                  )
                : t("adminDashboard.libraries.searchDisabledTooltip", "Hidden from users")}
            </TooltipContent>
          </Tooltip>
        </div>
        {/* Indexing Enabled Toggle */}
        <div className="col-span-1 flex items-center justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Switch
                  aria-label={`${library.display_name} indexing`}
                  checked={library.indexing_enabled}
                  disabled={disableAll || isUpdating === library.id || !onToggleIndexingEnabled}
                  onCheckedChange={(checked) => onToggleIndexingEnabled?.(library.id, checked)}
                />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {library.indexing_enabled
                ? t(
                    "adminDashboard.libraries.indexingEnabledTooltip",
                    "Indexer is processing this library"
                  )
                : t(
                    "adminDashboard.libraries.indexingDisabledTooltip",
                    "Indexer will skip this library"
                  )}
            </TooltipContent>
          </Tooltip>
        </div>
        {/* Status Badge */}
        <div className="col-span-1 flex items-center justify-center">
          {library.search_enabled ? (
            <span className="bg-success/10 text-success rounded-md px-2 py-0.5 text-xs font-medium">
              {t("adminDashboard.libraries.statusActive", "Active")}
            </span>
          ) : (
            <span className="bg-muted text-foreground rounded-md px-2 py-0.5 text-xs font-medium">
              {t("adminDashboard.libraries.statusHidden", "Hidden")}
            </span>
          )}
        </div>
      </div>

      {isStatsExpanded && (
        <LibraryStatsPanel error={statsError} isLoading={statsLoading} stats={statsData} />
      )}
    </div>
  )
}

export function LibrariesTable({
  libraries,
  onToggleSearchEnabled,
  onToggleIndexingEnabled,
  onEditAccess,
  onEdit,
  onDelete,
  onBrowseDocuments,
  isUpdating,
  disableAll,
  canManageIndexing = true,
}: {
  libraries: LibraryTableItem[]
  onToggleSearchEnabled?: (libraryId: string, enabled: boolean) => void
  onToggleIndexingEnabled?: (libraryId: string, enabled: boolean) => void
  onEditAccess?: (libraryId: string) => void
  onEdit?: (library: LibraryTableItem) => void
  onDelete?: (library: LibraryTableItem) => void
  onBrowseDocuments?: (libraryId: string, libraryName: string) => void
  isUpdating?: string
  disableAll?: boolean
  /** Tree backfill is an indexing run (``indexing`` capability); hide it otherwise. */
  canManageIndexing?: boolean
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isExpanded, setIsExpanded] = useState(false)
  const [expandedStatsLibrary, setExpandedStatsLibrary] = useState<string | null>(null)
  const [treeModalOpen, setTreeModalOpen] = useState(false)
  const [treeModalLibrary, setTreeModalLibrary] = useState<LibraryTableItem | null>(null)
  const [treeModalJobId, setTreeModalJobId] = useState<string | null>(null)
  const [startingBackfillForId, setStartingBackfillForId] = useState<string | null>(null)

  const startBackfillMutation = useStartLibraryTreeBackfillMutation()
  // Single batch query replaces the prior per-row N+1 active-job fan-out.
  const { data: activeBackfills } = useAllActiveBackfillsQuery(libraries.length > 0)
  // Single batch query populates the "Trees" column eagerly for every row.
  const { data: treeCoverageMap } = useAllTreeCoverageQuery(libraries.length > 0)

  // Sort libraries by name (case-insensitive)
  const sortedLibraries = useMemo(() => {
    if (libraries.length === 0) return []
    return [...libraries].sort((a, b) =>
      a.display_name.toLowerCase().localeCompare(b.display_name.toLowerCase())
    )
  }, [libraries])

  const controls = useAdminTableControls(sortedLibraries, {
    getName: (row) => row.display_name,
    getSearchText: (row) => row.display_name,
    isEnabled: (row) => row.search_enabled,
  })
  const displayLibraries = isExpanded ? controls.rows : controls.rows.slice(0, 5)
  const hasMoreLibraries = controls.count > 5

  if (libraries.length === 0) {
    return (
      <EmptyState
        description={t("adminDashboard.libraries.emptyDescription", "No libraries configured")}
        icon={Database}
        title={t("adminDashboard.libraries.emptyTitle", "No Libraries")}
      />
    )
  }

  async function openTreeBackfillForLibrary(
    library: LibraryTableItem,
    existingJobId?: string | null
  ) {
    // If the batch query already reports a running job for this row, open the
    // modal on that job_id directly — no POST required.
    if (existingJobId) {
      setTreeModalLibrary(library)
      setTreeModalJobId(existingJobId)
      setTreeModalOpen(true)
      return
    }

    // Defer opening the modal until the start mutation resolves so the dialog
    // never flashes "0/0 · no job" during the few hundred ms round trip.
    setStartingBackfillForId(library.id)
    try {
      const res = await startBackfillMutation.mutateAsync({ libraryId: library.id })
      setTreeModalLibrary(library)
      setTreeModalJobId(res.job_id)
      setTreeModalOpen(true)
    } catch (err: unknown) {
      const anyErr = err as { response?: { status?: number; data?: { job_id?: string } } }
      const status = anyErr.response?.status
      const raceJobId = anyErr.response?.data?.job_id
      if (status === 409 && typeof raceJobId === "string" && raceJobId.length > 0) {
        toast.info(
          t(
            "libraries.treeBackfill.reusingExisting",
            "A backfill was already running for this library — showing its progress."
          )
        )
        setTreeModalLibrary(library)
        setTreeModalJobId(raceJobId)
        setTreeModalOpen(true)
        return
      }
      console.error("Failed to start tree backfill", err)
      toast.error(
        t("libraries.treeBackfill.startFailed", "Failed to start tree backfill. See console.")
      )
    } finally {
      setStartingBackfillForId(null)
    }
  }

  return (
    <div className="space-y-2">
      <LibraryTreeBackfillModal
        isOpen={treeModalOpen}
        jobId={treeModalJobId}
        libraryId={treeModalLibrary?.id ?? null}
        libraryName={treeModalLibrary?.display_name ?? null}
        onJobChanged={(newJobId) => setTreeModalJobId(newJobId)}
        onOrphanLockCleared={() => setTreeModalJobId(null)}
        onOpenChange={(open) => {
          setTreeModalOpen(open)
          if (!open) {
            const libId = treeModalLibrary?.id ?? null
            setTreeModalJobId(null)
            setTreeModalLibrary(null)
            void queryClient.invalidateQueries({
              queryKey: adminQueryKeys.libraryTreeBackfillActiveAll(),
            })
            if (libId) {
              void queryClient.invalidateQueries({
                queryKey: adminQueryKeys.libraryTreeCoverage(libId),
              })
            }
          }
        }}
      />
      <AdminTableToolbar controls={controls} showFilters={false} />
      {/* Header Row */}
      <div className="text-muted-foreground hidden grid-cols-12 gap-3 px-4 py-2 text-xs font-medium tracking-wide uppercase lg:grid">
        <div className="col-span-2">{t("adminDashboard.libraries.name", "Name")}</div>
        <div className="col-span-1 text-center">{t("adminDashboard.libraries.type", "Type")}</div>
        <div className="col-span-3">{t("adminDashboard.libraries.description", "Description")}</div>
        <div className="col-span-1 text-center">
          {t("adminDashboard.libraries.documents", "Docs")}
        </div>
        <div className="col-span-1 text-center">{t("libraries.treeCoverage.header", "Trees")}</div>
        <div className="col-span-1 text-center">
          {t("adminDashboard.libraries.actions", "Actions")}
        </div>
        <div className="col-span-1 text-center">
          {t("adminDashboard.libraries.searchEnabled", "Search")}
        </div>
        <div className="col-span-1 text-center">
          {t("adminDashboard.libraries.indexingEnabled", "Indexing")}
        </div>
        <div className="col-span-1 text-center">
          {t("adminDashboard.libraries.status", "Status")}
        </div>
      </div>

      <div
        data-testid="admin-libraries-scroll-area"
        className={cn(
          "space-y-2",
          isExpanded && "max-h-[min(640px,_65svh)] overflow-y-auto overscroll-contain pr-1"
        )}
      >
        {displayLibraries.map((library) => (
          <LibraryRow
            key={library.id}
            activeJobId={activeBackfills?.[library.id] ?? null}
            disableAll={disableAll}
            isStartingBackfill={startingBackfillForId === library.id}
            isStatsExpanded={expandedStatsLibrary === library.id}
            isUpdating={isUpdating}
            library={library}
            treeCoverage={treeCoverageMap?.[library.id] ?? null}
            onBrowseDocuments={onBrowseDocuments}
            onDelete={onDelete}
            onEdit={onEdit}
            onEditAccess={onEditAccess}
            onOpenTreeBackfill={canManageIndexing ? openTreeBackfillForLibrary : undefined}
            onToggleIndexingEnabled={onToggleIndexingEnabled}
            onToggleSearchEnabled={onToggleSearchEnabled}
            onToggleStats={() =>
              setExpandedStatsLibrary(expandedStatsLibrary === library.id ? null : library.id)
            }
          />
        ))}
      </div>

      {hasMoreLibraries && (
        <div className="flex justify-center pt-2">
          <Button
            className="w-full border-none shadow-none"
            variant="outline"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="mr-2 size-4" />
                {t("adminDashboard.libraries.showLess", "Show Less")}
              </>
            ) : (
              <>
                <ChevronDown className="mr-2 size-4" />
                {t("adminDashboard.libraries.showMore", "Show More", {
                  count: sortedLibraries.length - 5,
                })}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
